// TSK-5163: Advance / Pre-Order "Cancel Order" — one routine shared by the retail
// (ManagePreorder) and restaurant (Preorder) screens.
//
// The backend cancelPreOrder sets the order to CAN and, when the order has a payment,
// posts a negative fin_receipt for the paid amount against the cash method. It does not
// know the till session, so the cash leaving the drawer is recorded here as a till
// cash-out (same shape as useCashManagement.handleCashInOut). Close Till reads both
// db.cashInCashOut (tillCloseService) and tillEvents.cashInOutData (CloseTill screens).
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { cancelPreorder, getAdvanceOrderCancelState } from "../services";
import useSessionStore from "../stores/useSessionStore";

export const CANCEL_MESSAGES = {
  offline: "Cancel requires an active connection. Please try again when online.",
  alreadyCancelled: "This order is already cancelled",
  completed: "Completed orders cannot be cancelled",
  fulfilled: "This order has fulfilled items and cannot be cancelled",
  unsynced: "This order has changes not yet synced. Please wait for sync and try again.",
  stateUnavailable: "Could not read the order from the server. Please try again.",
  failed: "Order could not be cancelled. Please try again.",
};

const CANCELLED_STATUSES = ["CAN", "VO"];

const isTrue = (value) => value === true || value === "Y" || value === "true";

// Returns the message that blocks the cancel, or null when the order may be cancelled.
// order = the server's salesHistoryDetails row (getAdvanceOrderCancelState).
export const getAdvanceCancelBlockReason = (order) => {
  if (!order) return CANCEL_MESSAGES.stateUnavailable;
  if (CANCELLED_STATUSES.includes(order.docstatus)) return CANCEL_MESSAGES.alreadyCancelled;
  if (isTrue(order.isinvoiced) || order.invoiceId) return CANCEL_MESSAGES.completed;
  const lines = Array.isArray(order.line) ? order.line : [];
  if (lines.some((line) => parseFloat(line?.deliveredQty) > 0)) return CANCEL_MESSAGES.fulfilled;
  return null;
};

// The advance actually paid — what cancelPreOrder refunds (fin_receiptplan.paidamt, which is
// the net sum of the plan's detail rows). salesHistoryDetails returns finReceiptPlan as a
// list ([FinReceiptPlan]) and does not expose paidamt, so sum every detail of every plan.
export const sumAdvancePaid = (order) => {
  const plans = [].concat(order?.finReceiptPlan || []);
  const total = plans.reduce((sum, plan) => {
    const details = Array.isArray(plan?.finReceiptPlanDetails) ? plan.finReceiptPlanDetails : [];
    return details.reduce((acc, row) => acc + (parseFloat(row?.amount) || 0), sum);
  }, 0);
  return Math.max(0, Math.round(total * 100) / 100);
};

// cancelPreOrder answers 200 for both cases; only the message tells them apart
// ("... refund receipt created ..." vs "... No payment found ...").
export const isRefundPosted = (result) => {
  const text = String(result?.message || "").toLowerCase();
  if (text.includes("no payment")) return false;
  return text.includes("receipt");
};

// Local orders row written by this till for the order. A row not yet synced means the
// server may not hold this till's latest change — cancelling now would race the sync.
const findUnsyncedLocalOrder = async (db, sOrderID) => {
  const rows = await db.orders.where("sOrderID").equals(sOrderID).toArray();
  return rows.find((row) => row?.isSynced !== 1) || null;
};

export const recordAdvanceCancelRefund = async (db, { amount, documentno }) => {
  const tillSessionId = useSessionStore.getState()?.tillSession?.tillSessionId;
  const entry = {
    key: uuidv4().replace(/-/g, "").toUpperCase(),
    id: uuidv4().replace(/-/g, "").toUpperCase(),
    date: dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss"),
    type: "cashOut",
    amount,
    note: `Advance cancel refund - ${documentno || ""}`.trim(),
  };
  await db.cashInCashOut.add(entry);

  const totals = { cashIn: 0, cashOut: 0, pettyCashIn: 0, pettyCashOut: 0 };
  const all = await db.cashInCashOut.toArray();
  all.forEach((item) => {
    if (Object.prototype.hasOwnProperty.call(totals, item.type)) {
      totals[item.type] += parseFloat(item.amount) || 0;
    }
  });
  if (!tillSessionId) {
    // The cash-out row above still reaches Close Till through tillCloseService.
    console.error("[TSK-5163] no till session in store; tillEvents.cashInOutData not updated", { key: entry.key });
    return entry;
  }
  await db.tillEvents
    .where("tillSessionId")
    .equals(tillSessionId)
    .modify((tillEvent) => {
      tillEvent.cashInOutData = totals;
    });
  return entry;
};

export const markLocalOrderCancelled = (db, sOrderID) =>
  db.orders.where("sOrderID").equals(sOrderID).modify({ docstatus: "CAN", isSynced: 1 });

const formatAmount = (amount) => (Number(amount) || 0).toFixed(2);

// Runs the whole cancel. Never throws; the caller shows `message` with the given level.
// Result: { ok, blocked, level: "success"|"warning"|"error", message, refundAmount, refundRecorded, documentno }
export const runAdvanceOrderCancel = async ({ sOrderID, reason, db, isOnline, currencySymbol = "Rs.", log = console }) => {
  const online = typeof isOnline === "boolean" ? isOnline : typeof navigator === "undefined" ? true : navigator.onLine !== false;
  if (!online) {
    return { ok: false, blocked: true, level: "error", message: CANCEL_MESSAGES.offline };
  }

  try {
    if (await findUnsyncedLocalOrder(db, sOrderID)) {
      return { ok: false, blocked: true, level: "error", message: CANCEL_MESSAGES.unsynced };
    }
  } catch (err) {
    log.error("[TSK-5163] local orders read failed before cancel", { sOrderID, error: err?.message || err });
    return { ok: false, blocked: true, level: "error", message: CANCEL_MESSAGES.stateUnavailable };
  }

  let order;
  try {
    order = await getAdvanceOrderCancelState(sOrderID);
  } catch (err) {
    log.error("[TSK-5163] order state read failed before cancel", { sOrderID, error: err?.message || err });
    return { ok: false, blocked: true, level: "error", message: CANCEL_MESSAGES.stateUnavailable };
  }
  const blockReason = getAdvanceCancelBlockReason(order);
  if (blockReason) {
    return { ok: false, blocked: true, level: "error", message: blockReason };
  }

  const documentno = order.documentno || sOrderID;
  const refundAmount = sumAdvancePaid(order);

  let result;
  try {
    result = await cancelPreorder(sOrderID, reason);
  } catch (err) {
    log.error("[TSK-5163] cancelPreOrder call failed", { sOrderID, error: err?.message || err });
    return { ok: false, blocked: false, level: "error", message: CANCEL_MESSAGES.failed };
  }
  if (String(result?.status) !== "200") {
    return { ok: false, blocked: false, level: "error", message: result?.message || CANCEL_MESSAGES.failed };
  }

  // Server has cancelled. Local writes below must not turn this into a failure.
  try {
    await markLocalOrderCancelled(db, sOrderID);
  } catch (err) {
    log.error("[TSK-5163] local order CAN update failed", { sOrderID, error: err?.message || err });
  }

  const refundPosted = isRefundPosted(result);
  if (!refundPosted) {
    return { ok: true, blocked: false, level: "success", message: `Order ${documentno} cancelled.`, refundAmount: 0, refundRecorded: false, documentno };
  }

  if (!(refundAmount > 0)) {
    // Server posted a refund but the POS cannot see the paid amount (no paymentplan rows).
    log.error("[TSK-5163] refund posted but paid amount unknown; no till cash-out recorded", { sOrderID, documentno, serverMessage: result?.message });
    return {
      ok: true,
      blocked: false,
      level: "warning",
      message: `Order ${documentno} cancelled. A cash refund was posted but the amount could not be read; please check the back office and record the cash-out manually.`,
      refundAmount: 0,
      refundRecorded: false,
      documentno,
    };
  }

  try {
    await recordAdvanceCancelRefund(db, { amount: refundAmount, documentno });
  } catch (err) {
    log.error("[TSK-5163] till cash-out for cancel refund failed", { sOrderID, documentno, refundAmount, error: err?.message || err });
    return {
      ok: true,
      blocked: false,
      level: "warning",
      message: `Order ${documentno} cancelled and ${currencySymbol}${formatAmount(refundAmount)} refunded, but the till cash-out could not be saved. Please record it in Cash Management.`,
      refundAmount,
      refundRecorded: false,
      documentno,
    };
  }

  return {
    ok: true,
    blocked: false,
    level: "success",
    message: `Order ${documentno} cancelled. ${currencySymbol}${formatAmount(refundAmount)} refunded in cash.`,
    refundAmount,
    refundRecorded: true,
    documentno,
  };
};
